import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { render } from 'react-dom';
import {
  IconButton,
  IconType,
} from '../../Licenses/edit/components/IconButton';

interface UseInteractiveQuizProps {
  currentSectionCounter: number;
  iFrame: any;
  isContentLoading: boolean;
}

const getEditorBlocks = (iFrameRef: any, currentSectionCounter: number) => {
  const iframeDoc = iFrameRef.current.contentWindow.document;
  const editorBlocks = iframeDoc.querySelectorAll(
    `.content_section:nth-of-type(${
      currentSectionCounter + 1
    }) .editor-block-embed`,
  );
  return editorBlocks as HTMLElement[];
};

const getHref = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const editorBlocks = getEditorBlocks(iFrameRef, currentSectionCounter);
  if (!editorBlocks[questionIndex]) {
    return -1;
  }
  return JSON.parse(
    (editorBlocks[questionIndex] as any).querySelector('#href').textContent,
  );
};

const getHeight = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const editorBlocks = getEditorBlocks(iFrameRef, currentSectionCounter);
  if (!editorBlocks[questionIndex]) {
    return -1;
  }
  return JSON.parse(
    (editorBlocks[questionIndex] as any).querySelector('#height').textContent,
  );
};

const getTemplateSlug = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const editorBlocks = getEditorBlocks(iFrameRef, currentSectionCounter);
  if (!editorBlocks[questionIndex]) {
    return -1;
  }
  return JSON.parse(
    (editorBlocks[questionIndex] as any).querySelector('#template_slug')
      .textContent,
  );
};

const useEmbedEditor = ({
  currentSectionCounter,
  iFrame,
  isContentLoading,
}: UseInteractiveQuizProps) => {
  const user = useContext(AuthContext);
  const [doneSections, setDoneSections] = useState<number[]>([]);
  const [templateParentTodos, setTemplateParentTodos] = useState<
    Record<string, HTMLElement>
  >({});
  const [templateSlugSectionAndIndex, setTemplateSlugSectionAndIndex] =
    useState<Record<string, { section: number; index: number }>>({});

  const isTeacher = user?.roles.includes(Role.teacher);

  useEffect(() => {
    window.onmessage = (e) => {
      if (e && e.data && e.data.event === 'editor_run_tests') {
        const { templateSlug, passing } = e.data;
        const highlightSection = templateParentTodos[templateSlug];
        if (highlightSection) {
          const imageSection = highlightSection.querySelector('use');
          if (passing) {
            highlightSection.classList.add('todo');
            highlightSection.classList.remove('danger');
            if (imageSection) {
              imageSection.setAttributeNS(
                'http://www.w3.org/1999/xlink',
                'xlink:href',
                '/static/syllabus/check.svg#icon',
              );
            }
          } else {
            highlightSection.classList.remove('todo');
            highlightSection.classList.add('danger');
            if (imageSection) {
              imageSection.setAttributeNS(
                'http://www.w3.org/1999/xlink',
                'xlink:href',
                '/static/syllabus/info.svg#icon',
              );
            }
          }
        }
      } else if (e && e.data && e.data.event === 'editor_height') {
        const { templateSlug, height } = e.data;
        const iFrameData = templateSlugSectionAndIndex[templateSlug];
        if (iFrameData) {
          resizeIFrameToFitContent(
            iFrameData.section,
            iFrameData.index,
            height,
          );
        }
      }
    };
  }, []);

  useEffect(() => {
    if (isContentLoading) {
      return;
    }
    if (doneSections.includes(currentSectionCounter)) {
      return;
    }
    setDoneSections((d) => [...d, currentSectionCounter]);
    setTimeout(() => {
      if (!iFrame.current) {
        return;
      }
      editorInit();
    }, 1000);
  }, [isContentLoading, currentSectionCounter]);

  const editorInit = () => {
    const editorBlocks = getEditorBlocks(iFrame, currentSectionCounter);
    for (let i = 0; i < editorBlocks.length; i++) {
      initBlock(i);
    }
  };

  const resizeIFrameToFitContent = (
    sectionCounter: number,
    blockIndex: number,
    height: number,
  ) => {
    // TODO: fix this
    return;
    const id = 'editor' + sectionCounter + '-' + blockIndex;
    const iframeDoc = iFrame.current.contentWindow.document;
    const editorIframe = iframeDoc.querySelector(`#${id}`);

    editorIframe.height = height;
    editorIframe.style.height = height + ' px !important';
    editorIframe.style.minHeight = height + ' px !important';
  };

  const initBlock = async (blockIndex: number) => {
    const editorBlocks = getEditorBlocks(iFrame, currentSectionCounter);
    if (!editorBlocks[blockIndex]) {
      return;
    }

    const href =
      getHref(iFrame, currentSectionCounter, blockIndex) + '?is_small=true';

    const templateSlug = getTemplateSlug(
      iFrame,
      currentSectionCounter,
      blockIndex,
    );

    const height = getHeight(iFrame, currentSectionCounter, blockIndex);

    const container = editorBlocks[blockIndex]!.querySelector(
      '.editor-container',
    ) as HTMLElement;

    const containerNav = editorBlocks[blockIndex]!.parentElement!.querySelector(
      '.editor-block-nav',
    ) as HTMLElement;

    const titleElement = containerNav.parentElement?.parentElement?.children[0];
    let buttonContainer: any = null;

    if (titleElement) {
      const btnContainer = document.createElement('div');
      btnContainer.id = 'btn-container';
      titleElement.appendChild(btnContainer);
    }

    buttonContainer = titleElement?.querySelector('#btn-container');

    templateSlugSectionAndIndex[templateSlug] = {
      index: blockIndex,
      section: currentSectionCounter,
    };

    setTemplateSlugSectionAndIndex({ ...templateSlugSectionAndIndex });
    if (
      container &&
      container.parentNode &&
      container.parentNode.parentNode &&
      container.parentNode.parentNode.parentNode
    ) {
      const highlightSection = container.parentNode.parentNode
        .parentNode as HTMLElement;
      if (highlightSection.classList.contains('highlight_section')) {
        templateParentTodos[templateSlug] = highlightSection;
        setTemplateParentTodos(templateParentTodos);
        try {
          // TODO: editor dispatch old test event
          highlightSection.classList.remove('todo');
          const imageSection = highlightSection.querySelector('use');
          if (imageSection) {
            imageSection.setAttributeNS(
              'http://www.w3.org/1999/xlink',
              'xlink:href',
              '/static/syllabus/todo.svg#icon',
            );
          }
        } catch (e) {
          console.log(e);
        }
      }
    }

    const startCodingBtn = editorBlocks[blockIndex]!.querySelector(
      '.start-coding',
    ) as HTMLElement;

    const toggleTask = (display: boolean) => {
      const { children } = editorBlocks[blockIndex]!.parentElement!;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        if (
          child.classList.contains('editor-block-embed') ||
          child.classList.contains('editor-block-nav')
        ) {
          continue;
        }
        child.style.display = display ? 'block' : 'none';
      }

      render(
        <div
          style={{
            transform: 'scale(1.5)',
            position: 'absolute',
            right: '40px',
            top: '20px',
            overflow: 'hidden',
            borderRadius: '40px',
            cursor: 'pointer',
          }}
        >
          <IconButton
            onClick={() => {
              toggleTask(!display);
            }}
            type={display ? IconType.hide : IconType.show}
            style={{ padding: '6px' }}
            aria-label={display ? 'hide' : 'show'}
          />
        </div>,
        buttonContainer || containerNav,
      );
    };

    startCodingBtn.innerHTML += `<iframe
      style="height: 0; display: none"
      id="${'editor' + currentSectionCounter + '-' + currentSectionCounter}"
      src="${href}"
      scrolling="no"
      height="0"
    />`;

    startCodingBtn.addEventListener('click', () => {
      toggleTask(false);
      const sideMenuBtn = document.querySelector(
        "div[title='Close Side Menu']",
      );
      if (sideMenuBtn) {
        (sideMenuBtn as HTMLElement).click();
      }
      render(
        <>
          <div style={{ flex: 1 }}>
            <iframe
              style={{ height }}
              id={
                'editor' + currentSectionCounter + '-' + currentSectionCounter
              }
              src={href}
              scrolling="no"
              frameBorder={0}
              marginHeight={0}
              marginWidth={0}
              height={height}
            />
          </div>
        </>,
        container,
      );
    });
  };
};

export default useEmbedEditor;
